/**
 * Video Portfolio Section - Embeds YouTube/Vimeo videos
 * Uses React Portal for true full-screen modal
 */
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Video, Plus, Trash2, AlertCircle, X, Play } from "lucide-react";

/**
 * Extract video ID and platform from URL
 */
function parseVideoUrl(url) {
  if (!url) return null;
  
  // YouTube patterns
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
  ];
  
  for (const pattern of youtubePatterns) {
    const match = url.match(pattern);
    if (match) {
      return { platform: 'youtube', videoId: match[1] };
    }
  }
  
  // Vimeo patterns
  const vimeoPatterns = [
    /vimeo\.com\/(\d+)/,
    /player\.vimeo\.com\/video\/(\d+)/
  ];
  
  for (const pattern of vimeoPatterns) {
    const match = url.match(pattern);
    if (match) {
      return { platform: 'vimeo', videoId: match[1] };
    }
  }
  
  return null;
}

/**
 * Get embed URL for a video (with parameters to hide suggestions)
 */
function getEmbedUrl(parsed, autoplay = false) {
  if (!parsed) return null;
  
  if (parsed.platform === 'youtube') {
    const autoplayParam = autoplay ? '&autoplay=1' : '';
    return `https://www.youtube.com/embed/${parsed.videoId}?rel=0&modestbranding=1${autoplayParam}`;
  }
  if (parsed.platform === 'vimeo') {
    const autoplayParam = autoplay ? '?autoplay=1' : '';
    return `https://player.vimeo.com/video/${parsed.videoId}${autoplayParam}`;
  }
  return null;
}

/**
 * Get thumbnail URL for a video
 */
function getThumbnailUrl(parsed) {
  if (!parsed) return null;
  
  if (parsed.platform === 'youtube') {
    return `https://img.youtube.com/vi/${parsed.videoId}/hqdefault.jpg`;
  }
  return null;
}

/**
 * Full-screen Video Modal - rendered via Portal to escape container constraints
 */
function VideoModal({ video, onClose }) {
  const parsed = parseVideoUrl(video.url);
  const embedUrl = getEmbedUrl(parsed, true);
  
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden'; // Prevent background scroll
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  
  if (!embedUrl) return null;
  
  const modalContent = (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={onClose}
    >
      {/* Close button - top right */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 24px',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          border: 'none',
          borderRadius: '8px',
          color: 'white',
          fontSize: '16px',
          cursor: 'pointer',
          zIndex: 100000
        }}
        data-testid="close-video-modal"
      >
        <X style={{ width: '24px', height: '24px' }} />
        Close
      </button>
      
      {/* Title */}
      {video.title && (
        <h3 style={{
          color: 'white',
          fontSize: '20px',
          fontWeight: '500',
          marginBottom: '16px',
          textAlign: 'center'
        }}>
          {video.title}
        </h3>
      )}
      
      {/* Video container */}
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '1200px',
          aspectRatio: '16/9',
          backgroundColor: 'black',
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <iframe
          src={embedUrl}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none'
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          title={video.title || 'Video'}
        />
      </div>
      
      {/* Platform badge */}
      <div style={{
        marginTop: '16px',
        padding: '6px 12px',
        backgroundColor: parsed.platform === 'youtube' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.2)',
        color: parsed.platform === 'youtube' ? '#f87171' : '#60a5fa',
        borderRadius: '4px',
        fontSize: '14px',
        fontWeight: '500'
      }}>
        {parsed.platform === 'youtube' ? 'YouTube' : 'Vimeo'}
      </div>
      
      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px', marginTop: '16px' }}>
        Press ESC or click outside to close
      </p>
    </div>
  );
  
  // Use portal to render at document root
  return createPortal(modalContent, document.body);
}

/**
 * Video Thumbnail Card - shows thumbnail with play button overlay
 */
function VideoThumbnailCard({ video, onRemove, showRemove = true, onPlay }) {
  const parsed = parseVideoUrl(video.url);
  const thumbnailUrl = getThumbnailUrl(parsed);
  
  if (!parsed) {
    return (
      <Card className="bg-white/[0.03] border-white/10 p-4">
        <div className="flex items-center gap-3 text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">Invalid video URL</span>
        </div>
        {showRemove && onRemove && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="mt-2 text-red-400 hover:text-red-300"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Remove
          </Button>
        )}
      </Card>
    );
  }
  
  return (
    <Card className="bg-white/[0.03] border-white/10 overflow-hidden group">
      {/* Thumbnail with play button overlay */}
      <div 
        className="relative aspect-video bg-black cursor-pointer"
        onClick={onPlay}
      >
        {thumbnailUrl ? (
          <img 
            src={thumbnailUrl} 
            alt={video.title || 'Video thumbnail'}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 to-purple-900/50 flex items-center justify-center">
            <Video className="w-12 h-12 text-white/30" />
          </div>
        )}
        
        {/* Play button overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/20 transition-colors">
          <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
            <Play className="w-8 h-8 text-black ml-1" fill="black" />
          </div>
        </div>
      </div>
      
      {/* Info bar */}
      <div className="p-3 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
            parsed.platform === 'youtube' 
              ? 'bg-red-500/20 text-red-400' 
              : 'bg-blue-500/20 text-blue-400'
          }`}>
            {parsed.platform === 'youtube' ? 'YouTube' : 'Vimeo'}
          </span>
          {video.title && (
            <span className="text-sm text-white/70 truncate">{video.title}</span>
          )}
        </div>
        
        {showRemove && onRemove && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="text-white/40 hover:text-red-400 shrink-0"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </Card>
  );
}

/**
 * Video Portfolio Section for Profile Editing
 */
export default function VideoPortfolioSection({ videos = [], setVideos }) {
  const [newUrl, setNewUrl] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [error, setError] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);
  
  const handleAddVideo = () => {
    setError("");
    
    if (!newUrl.trim()) {
      setError("Please enter a video URL");
      return;
    }
    
    const parsed = parseVideoUrl(newUrl);
    if (!parsed) {
      setError("Invalid URL. Please use a YouTube or Vimeo link.");
      return;
    }
    
    const isDuplicate = videos.some(v => {
      const existingParsed = parseVideoUrl(v.url);
      return existingParsed && 
             existingParsed.platform === parsed.platform && 
             existingParsed.videoId === parsed.videoId;
    });
    
    if (isDuplicate) {
      setError("This video is already in your portfolio");
      return;
    }
    
    setVideos([...videos, { url: newUrl.trim(), title: newTitle.trim() || null }]);
    setNewUrl("");
    setNewTitle("");
  };
  
  const handleRemove = (index) => {
    const newVideos = [...videos];
    newVideos.splice(index, 1);
    setVideos(newVideos);
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Video className="w-5 h-5 text-coral" />
        <h2 className="text-xl font-semibold">Video Portfolio</h2>
      </div>
      
      <p className="text-white/50 text-sm mb-4">
        Add YouTube or Vimeo links to showcase your video work
      </p>
      
      <Card className="bg-white/[0.03] border-white/10 p-4 space-y-3">
        <div>
          <label className="text-sm text-white/60 block mb-1">Video URL</label>
          <Input
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
            className="bg-white/[0.05] border-white/10 text-white"
            data-testid="video-url-input"
          />
        </div>
        
        <div>
          <label className="text-sm text-white/60 block mb-1">Title (optional)</label>
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Wedding Highlight Reel"
            className="bg-white/[0.05] border-white/10 text-white"
            data-testid="video-title-input"
          />
        </div>
        
        {error && (
          <div className="flex items-center gap-2 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}
        
        <Button
          type="button"
          onClick={handleAddVideo}
          className="bg-coral hover:bg-coral/90 text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Video
        </Button>
      </Card>
      
      {videos.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {videos.map((video, index) => (
            <VideoThumbnailCard
              key={index}
              video={video}
              onRemove={() => handleRemove(index)}
              onPlay={() => setSelectedVideo(video)}
            />
          ))}
        </div>
      )}
      
      {videos.length === 0 && (
        <div className="text-center py-8 text-white/30">
          <Video className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>No videos added yet</p>
        </div>
      )}
      
      {selectedVideo && (
        <VideoModal 
          video={selectedVideo} 
          onClose={() => setSelectedVideo(null)} 
        />
      )}
    </div>
  );
}

/**
 * Video Portfolio Display for Profile View (read-only)
 */
export function VideoPortfolioDisplay({ videos }) {
  const [selectedVideo, setSelectedVideo] = useState(null);
  
  if (!videos || videos.length === 0) return null;
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold flex items-center gap-2">
        <Video className="w-5 h-5 text-coral" />
        Portfolio Videos
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {videos.map((video, index) => (
          <VideoThumbnailCard 
            key={index} 
            video={video} 
            showRemove={false}
            onPlay={() => setSelectedVideo(video)}
          />
        ))}
      </div>
      
      {selectedVideo && (
        <VideoModal 
          video={selectedVideo} 
          onClose={() => setSelectedVideo(null)} 
        />
      )}
    </div>
  );
}

export { parseVideoUrl, getEmbedUrl, getThumbnailUrl };
